import React, { useEffect, useState } from "react";
import { Play, RefreshCw, Zap, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import Topbar from "@/components/layout/Topbar";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";

const STATUS_STYLES = {
  success: "bg-emerald-100 text-emerald-700",
  partial: "bg-yellow-100 text-yellow-800",
  failed: "bg-red-100 text-red-700",
  running: "bg-blue-100 text-blue-700",
};

const STATUS_ICON = {
  success: CheckCircle2,
  partial: AlertCircle,
  failed: XCircle,
  running: RefreshCw,
};

export default function EtlJobs() {
  const [connectors, setConnectors] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        api.get("/etl/connectors"),
        api.get("/etl/runs?limit=50"),
      ]);
      setConnectors(c.data);
      setRuns(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const runConnector = async (name) => {
    setRunning(name);
    try {
      const { data } = await api.post(`/etl/run/${name}`);
      toast.success(
        `${name} · ${data.status} · +${data.inserted || 0} new / ${data.updated || 0} updated / ${data.skipped || 0} skipped`
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(null);
    }
  };

  return (
    <>
      <Toaster position="top-right" richColors />
      <Topbar
        title="ETL Jobs"
        subtitle="Data ingestion from live ERP systems"
        right={
          <button
            type="button"
            onClick={load}
            data-testid="etl-refresh"
            className="flex h-9 items-center gap-1.5 rounded-sm border border-slate-200 px-3 text-xs font-semibold uppercase tracking-widest text-slate-600 hover:border-navy-600 hover:text-navy-600"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
        }
      />

      <div className="space-y-6 p-6 animate-fade-in" data-testid="etl-jobs">
        {/* Connectors */}
        <section>
          <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Connectors
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {connectors.map((c) => (
              <div
                key={c.source_system}
                className="flex flex-col rounded-md border border-slate-200 bg-white p-5 shadow-sm"
                data-testid={`connector-${c.source_system.toLowerCase()}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-navy-600 text-white">
                      <Zap className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="font-display text-lg font-semibold text-navy-600">
                        {c.source_system}
                      </div>
                      <div className="text-xs text-slate-500">
                        env: {c.env_prefix}_API_BASE / _API_KEY
                      </div>
                    </div>
                  </div>
                  <span
                    className={[
                      "rounded-sm px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest",
                      c.mode === "live"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-slate-100 text-slate-600",
                    ].join(" ")}
                  >
                    {c.mode}
                  </span>
                </div>
                <div className="mt-4 truncate text-xs text-slate-500">
                  {c.api_base || "No live endpoint configured — running in mock mode"}
                </div>
                <button
                  type="button"
                  onClick={() => runConnector(c.source_system)}
                  disabled={running === c.source_system}
                  data-testid={`run-${c.source_system.toLowerCase()}`}
                  className="mt-4 flex h-9 items-center justify-center gap-2 rounded-sm bg-brand-orange px-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#C1701C] disabled:opacity-60"
                >
                  {running === c.source_system ? (
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Play className="h-3.5 w-3.5" />
                  )}
                  {running === c.source_system ? "Running…" : "Run now"}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Runs */}
        <section>
          <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Recent runs
          </div>
          <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3 text-left">Run</th>
                  <th className="px-5 py-3 text-left">Source</th>
                  <th className="px-5 py-3 text-left">Status</th>
                  <th className="px-5 py-3 text-left">Mode</th>
                  <th className="px-5 py-3 text-right">Extracted</th>
                  <th className="px-5 py-3 text-right">Inserted</th>
                  <th className="px-5 py-3 text-right">Updated</th>
                  <th className="px-5 py-3 text-right">Skipped</th>
                  <th className="px-5 py-3 text-left">Triggered By</th>
                  <th className="px-5 py-3 text-left">Started</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={9} className="px-5 py-10 text-center text-slate-400">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && runs.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-5 py-10 text-center text-slate-400">
                      No runs yet — kick one off from a connector above.
                    </td>
                  </tr>
                )}
                {runs.map((r) => {
                  const Icon = STATUS_ICON[r.status] || AlertCircle;
                  return (
                    <tr
                      key={r.run_id}
                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                      data-testid={`run-row-${r.run_id}`}
                    >
                      <td className="px-5 py-3 mono text-xs text-slate-500">
                        {r.run_id}
                      </td>
                      <td className="px-5 py-3 font-medium text-navy-600">
                        {r.source_system}
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className={`inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
                            STATUS_STYLES[r.status] || "bg-slate-100 text-slate-600"
                          }`}
                        >
                          <Icon className="h-3 w-3" />
                          {r.status}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-xs uppercase tracking-widest text-slate-500">
                        {r.mode}
                      </td>
                      <td className="px-5 py-3 text-right tabular text-slate-600">
                        {r.extracted}
                      </td>
                      <td className="px-5 py-3 text-right tabular font-medium text-emerald-600">
                        {r.inserted ?? 0}
                      </td>
                      <td className="px-5 py-3 text-right tabular font-medium text-brand-orange">
                        {r.updated ?? 0}
                      </td>
                      <td className="px-5 py-3 text-right tabular text-slate-500">
                        {r.skipped}
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-500">
                        {r.triggered_by}
                      </td>
                      <td className="whitespace-nowrap px-5 py-3 text-xs text-slate-500 tabular">
                        {new Date(r.started_at).toLocaleString("en-US", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  );
}
